import { useRef, type ReactNode } from 'react'
import { motion, useMotionValue, useTransform, type PanInfo } from 'framer-motion'
import { FluentEmoji } from './fluent-emoji'
import { hapticLight, hapticMedium } from '@/lib/native'

export interface SwipeAction {
  icon: string
  color: string
  backgroundColor: string
  onAction: () => void
  label: string
}

interface SwipeableRowProps {
  children: ReactNode
  leftAction?: SwipeAction  // Revealed when swiping LEFT (appears on RIGHT side)
  rightAction?: SwipeAction // Revealed when swiping RIGHT (appears on LEFT side)
  threshold?: number
  maxSwipe?: number
  enableHaptic?: boolean
  disabled?: boolean
}

export function SwipeableRow({
  children,
  leftAction,
  rightAction,
  threshold = 80,
  maxSwipe = 120,
  enableHaptic = true,
  disabled = false,
}: SwipeableRowProps) {
  const x = useMotionValue(0)
  const hapticTriggeredRef = useRef(false)
  
  // Transform for action reveal - leftAction appears on RIGHT when swiping left (negative x)
  // Only show background when actually swiping (x < 0)
  const leftBgOpacity = useTransform(x, [-10, 0], [1, 0])
  const leftActionOpacity = useTransform(x, [-threshold, -20], [1, 0])
  const leftActionScale = useTransform(x, [-threshold, -threshold/2, 0], [1, 0.8, 0.5])
  
  // rightAction appears on LEFT when swiping right (positive x)
  const rightBgOpacity = useTransform(x, [0, 10], [0, 1])
  const rightActionOpacity = useTransform(x, [20, threshold], [0, 1])
  const rightActionScale = useTransform(x, [0, threshold/2, threshold], [0.5, 0.8, 1])

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const offset = info.offset.x
    const velocity = info.velocity.x
    
    // Check if swipe threshold reached
    if (Math.abs(offset) >= threshold || Math.abs(velocity) > 500) {
      if (offset < 0 && leftAction) {
        // Swiped LEFT - trigger left action (e.g., delete on right side)
        if (enableHaptic) hapticMedium()
        leftAction.onAction()
      } else if (offset > 0 && rightAction) {
        // Swiped RIGHT - trigger right action (e.g., mark read on left side)
        if (enableHaptic) hapticMedium()
        rightAction.onAction()
      }
    }
    
    // Reset position
    x.set(0)
    hapticTriggeredRef.current = false
  }

  const handleDrag = (_: unknown, info: PanInfo) => {
    const offset = info.offset.x
    
    // Trigger haptic at threshold
    if (!hapticTriggeredRef.current && Math.abs(offset) >= threshold && enableHaptic) {
      hapticLight()
      hapticTriggeredRef.current = true
    }
  }

  if (disabled) {
    return <>{children}</>
  }

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Left action background - appears on RIGHT side when swiping LEFT */}
      {leftAction && (
        <motion.div
          className="absolute inset-0 flex items-center justify-end rounded-xl"
          style={{
            backgroundColor: leftAction.backgroundColor,
            opacity: leftBgOpacity,
          }}
        >
          <motion.div 
            className="flex flex-col items-center gap-1 pr-6"
            style={{
              opacity: leftActionOpacity,
              scale: leftActionScale,
            }}
          >
            <FluentEmoji emoji={leftAction.icon} size={24} />
            <span className={`text-xs font-semibold ${leftAction.color}`}>
              {leftAction.label}
            </span>
          </motion.div>
        </motion.div>
      )}
      
      {/* Right action background - appears on LEFT side when swiping RIGHT */}
      {rightAction && (
        <motion.div
          className="absolute inset-0 flex items-center justify-start rounded-xl"
          style={{
            backgroundColor: rightAction.backgroundColor,
            opacity: rightBgOpacity,
          }}
        >
          <motion.div 
            className="flex flex-col items-center gap-1 pl-6"
            style={{
              opacity: rightActionOpacity,
              scale: rightActionScale,
            }}
          >
            <FluentEmoji emoji={rightAction.icon} size={24} />
            <span className={`text-xs font-semibold ${rightAction.color}`}>
              {rightAction.label}
            </span>
          </motion.div>
        </motion.div>
      )}
      
      {/* Swipeable content */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{
          left: leftAction ? -maxSwipe : 0,
          right: rightAction ? maxSwipe : 0,
        }}
        dragElastic={0.2}
        dragSnapToOrigin={false}
        onDirectionLock={(axis) => {
          if (axis === 'y') x.set(0)
        }}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        style={{ x, touchAction: 'pan-y' }}
        className="relative rounded-xl"
      >
        {children}
      </motion.div>
    </div>
  )
}
